import { differenceInDays, parseISO, startOfDay } from "date-fns";

import { Gestation } from "@/pregnancy/types";

import { OVERDUE_WEEKS } from "../constants/gestational-age";

import { deriveLastMenstrualPeriodFromEDD } from "./lmp";

// --- Calculate ---------------------------------------------------------------

/**
 * Calculates gestational age in weeks and days from an EDD and an optional reference date.
 * Both dates are normalised to the start of their local calendar day.
 *
 * @param eddDateString - ISO date or datetime string for the estimated delivery date
 * @param referenceDate - Date to evaluate gestational age at; defaults to today when omitted
 * @returns `Gestation` with `weeks` and `days` (may be negative when reference precedes the LMP)
 */
export function calculateGestationalAgeInWeeksAndDays(
  eddDateString: string,
  referenceDate?: Date | string,
): Gestation {
  if (!referenceDate) {
    referenceDate = startOfDay(new Date());
  } else if (typeof referenceDate === "string") {
    referenceDate = startOfDay(parseISO(referenceDate));
  } else {
    referenceDate = startOfDay(referenceDate);
  }

  const lastMenstrualPeriod = deriveLastMenstrualPeriodFromEDD(eddDateString);
  const totalDays = differenceInDays(startOfDay(referenceDate), startOfDay(lastMenstrualPeriod));

  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
}

/**
 * Formats weeks and days as a compact age string (e.g. `"12w 3d"` or `"+3w 2d"` with a prefix).
 * Returns `"--"` for negative, infinite, or NaN values.
 *
 * @param weeks - Whole weeks component
 * @param days - Remaining days component
 * @param prefix - Optional string prepended to the result (e.g. `"+"` for postpartum)
 * @returns Formatted age string or `"--"` when the values are invalid
 */
function getAgeDisplay(weeks: number, days: number, prefix: string = ""): string {
  if (weeks < 0 || days < 0 || !Number.isFinite(weeks) || !Number.isFinite(days)) return "--";
  return `${prefix}${weeks}w ${days}d`;
}

/**
 * Formats a `Gestation` value as a human-readable string (e.g. `"12w 3d"`).
 * Returns `"--"` for negative or non-finite values.
 *
 * @param gestationalAge - `Gestation` object with `weeks` and `days`
 * @returns Formatted string such as `"12w 3d"`, or `"--"` when the age cannot be displayed
 */
export function getGestationalAgeDisplay(gestationalAge: Gestation): string {
  return getAgeDisplay(gestationalAge.weeks, gestationalAge.days);
}

/**
 * Formats a postpartum `Gestation` as a prefixed string (e.g. `"+3w 2d"`).
 * The leading `+` distinguishes postpartum age from gestational age in the UI.
 * Returns `"--"` for negative or non-finite values.
 *
 * @param postpartumAge - `Gestation` object representing time elapsed since birth
 * @returns Formatted string such as `"+3w 2d"`, or `"--"` when the age cannot be displayed
 */
export function getPostpartumAgeDisplay(postpartumAge: Gestation): string {
  return getAgeDisplay(postpartumAge.weeks, postpartumAge.days, "+");
}

/**
 * Returns whether the gestational age is 42+ weeks for an active pregnancy.
 *
 * @param isPregnant - Whether the patient currently has an active pregnancy
 * @param gestation - Gestational age with a `weeks` field; treated as 0 when omitted
 * @returns `true` when the patient is pregnant and gestational age is 42+ weeks
 */
export function isGestationalAgeOverdue(
  isPregnant: boolean | undefined,
  gestation?: { weeks?: number | string; days?: number | string },
): boolean {
  return Boolean(isPregnant && Number(gestation?.weeks ?? 0) >= OVERDUE_WEEKS);
}

/**
 * Returns whether a raw week count is 42+ weeks.
 *
 * @param weeks - Gestational age in whole weeks
 * @returns `true` when `weeks` is 42 or greater
 */
export function isGestationalAgeWeeksOverdue(weeks: number): boolean {
  return weeks >= OVERDUE_WEEKS;
}

/**
 * Converts a decimal weeks value (e.g. `28.5`) to whole weeks and remaining days.
 * Handles the rounding edge case where the day component rounds up to 7
 * by carrying over to the next week (e.g. `28.999` → `29w 0d`).
 *
 * @param value - Gestational age expressed as decimal weeks
 * @returns `{ weeks, days }` with normalized whole-number components
 */
export function decimalWeeksToWeeksDays(value: number): { weeks: number; days: number } {
  let weeks = Math.floor(value);
  let days = Math.round((value - weeks) * 7);

  // Normalize rounding edge case where days could be 7 (e.g., 28.999 weeks -> 29w 0d)
  if (days === 7) {
    weeks += 1;
    days = 0;
  }

  return { weeks, days };
}
